import { useState, useEffect } from "react";
import FormInput from "../ui/FormInput";
import Button from "../ui/Button";
import {
  createSociety,
  deleteSociety,
  getSocieties,
  updateSociety,
  uploadSocietyIcon,
} from "../../services/societyService";
import { getAmenities, createAmenity } from "../../services/amenityService";
import { getServices, createService } from "../../services/serviceService";
import { toast } from "react-toastify";
import styles from "./SocietyForm.module.css";
import { resolveBackendUrl } from "../../services/api";
import { societySchema, zodErrorToFieldErrors } from "../../schemas/societySchema";
import masterService from "../../services/masterEntitiesService";

const DEFAULT_MASTER_AMENITIES = [
  { name: "Swimming Pool", type: "activity", booking_fee: 0, max_booking_hours: 2, icon: "🏊" },
  { name: "Gymnasium & Fitness", type: "activity", booking_fee: 0, max_booking_hours: 2, icon: "🏋️" },
  { name: "Club House", type: "booking", booking_fee: 0, max_booking_hours: 3, icon: "🏛️" },
  { name: "Badminton Court", type: "activity", booking_fee: 0, max_booking_hours: 1, icon: "🏸" },
  { name: "Tennis Court", type: "activity", booking_fee: 0, max_booking_hours: 1, icon: "🎾" },
  { name: "Banquet & Party Hall", type: "booking", booking_fee: 500, max_booking_hours: 4, icon: "🎉" },
  { name: "Yoga & Meditation Studio", type: "activity", booking_fee: 0, max_booking_hours: 1, icon: "🧘" },
  { name: "Children's Play Area", type: "activity", booking_fee: 0, max_booking_hours: 2, icon: "🛝" },
  { name: "Amphitheatre & Open Lawn", type: "booking", booking_fee: 0, max_booking_hours: 4, icon: "🎭" },
  { name: "Co-working & Conference Room", type: "booking", booking_fee: 0, max_booking_hours: 2, icon: "💼" },
];

const DEFAULT_MASTER_SERVICES = [
  { service_name: "Laundry & Dry Cleaning", icon: "🧺" },
  { service_name: "Plumbing Maintenance", icon: "🔧" },
  { service_name: "Electrical Repair", icon: "⚡" },
  { service_name: "Housekeeping & Cleaning", icon: "🧹" },
  { service_name: "Pest Control", icon: "🐜" },
  { service_name: "Carpentry & Furniture Repair", icon: "🪚" },
  { service_name: "Waste Management & Disposal", icon: "🗑️" },
  { service_name: "Security & Guard Patrol", icon: "🛡️" },
  { service_name: "Elevator & Lift Maintenance", icon: "🛗" },
  { service_name: "Gardening & Landscaping", icon: "🌿" },
];

const SocietyForm = ({ society, readOnly = false, onSuccess, onDeleteSuccess }) => {
  const [form, setForm] = useState({
    society_name: "",
    society_type: "",
    parent_society_id: "",
    property_status: "operational",
    city: "",
    state: "",
    country: "India",
    contact_number: "",
    contact_email: "",
    pincode: "",
    address: "",
    google_map_url: "",
    latitude: "",
    longitude: "",
  });

  const [allSocieties, setAllSocieties] = useState([]);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Master Data Toggles & Selection State
  const [enableAmenities, setEnableAmenities] = useState(false);
  const [enableServices, setEnableServices] = useState(false);
  const [masterAmenities, setMasterAmenities] = useState(DEFAULT_MASTER_AMENITIES);
  const [masterServices, setMasterServices] = useState(DEFAULT_MASTER_SERVICES);
  const [selectedAmenities, setSelectedAmenities] = useState(new Set());
  const [selectedServices, setSelectedServices] = useState(new Set());
  const [masterPropertyTypes, setMasterPropertyTypes] = useState([
    "Residential",
    "Commercial",
    "Hybrid (Residential + Commercial)",
    "Industrial",
    "Gated Villa Community",
    "Co-Working Space",
    "Retail & Mall"
  ]);

  useEffect(() => {
    masterService
      .getPropertyTypes()
      .then((types) => {
        if (types && Array.isArray(types)) {
          const names = types.filter((t) => t.is_active).map((t) => t.name);
          setMasterPropertyTypes((prev) => Array.from(new Set([...prev, ...names])));
        }
      })
      .catch((err) => console.warn("Could not load master property types:", err));
  }, []);

  useEffect(() => {
    let active = true;
    getSocieties()
      .then((data) => {
        if (active && Array.isArray(data)) setAllSocieties(data);
      })
      .catch(() => { });

    // Fetch master catalog of amenities and services
    Promise.all([
      getAmenities().catch(() => []),
      getServices().catch(() => []),
    ]).then(([amenitiesData, servicesData]) => {
      if (!active) return;
      // Merge amenities from DB with defaults
      const aMap = new Map();
      DEFAULT_MASTER_AMENITIES.forEach((a) => aMap.set(a.name.toLowerCase(), a));
      if (Array.isArray(amenitiesData)) {
        amenitiesData.forEach((a) => {
          if (a?.name) {
            const key = a.name.toLowerCase();
            aMap.set(key, { ...(aMap.get(key) || {}), ...a, icon: aMap.get(key)?.icon || "✨" });
          }
        });
      }
      setMasterAmenities(Array.from(aMap.values()));

      // Merge services from DB with defaults
      const sMap = new Map();
      DEFAULT_MASTER_SERVICES.forEach((s) => sMap.set(s.service_name.toLowerCase(), s));
      if (Array.isArray(servicesData)) {
        servicesData.forEach((s) => {
          if (s?.service_name) {
            const key = s.service_name.toLowerCase();
            sMap.set(key, { ...(sMap.get(key) || {}), ...s, icon: sMap.get(key)?.icon || "⚡" });
          }
        });
      }
      setMasterServices(Array.from(sMap.values()));
    });

    return () => {
      active = false;
    };
  }, []);

  /* ---------------- PREFILL (EDIT / VIEW) ---------------- */
  useEffect(() => {
    if (society) {
      setForm({
        society_name: society.society_name || "",
        society_type: society.society_type || "",
        parent_society_id: society.parent_society_id ? String(society.parent_society_id) : "",
        property_status: society.property_status || "operational",
        city: society.city || "",
        state: society.state || "",
        country: society.country || "India",
        contact_number: society.contact_number || "",
        contact_email: society.contact_email || "",
        pincode: society.pincode || "",
        address: society.address || "",
        google_map_url: society.google_map_url || "",
        latitude: society.latitude || "",
        longitude: society.longitude || "",
      });
      setIconPreview(resolveBackendUrl(society.icon_url || ""));
      setIconFile(null);

      // Fetch existing amenities and services for this property
      if (society.society_id) {
        getAmenities(society.society_id)
          .then((existing) => {
            if (Array.isArray(existing) && existing.length > 0) {
              setEnableAmenities(true);
              setSelectedAmenities(new Set(existing.map((a) => a.name)));
            }
          })
          .catch(() => { });

        getServices(society.society_id)
          .then((existing) => {
            if (Array.isArray(existing) && existing.length > 0) {
              setEnableServices(true);
              setSelectedServices(new Set(existing.map((s) => s.service_name)));
            }
          })
          .catch(() => { });
      }
    }
  }, [society]);

  /* ---------------- EXTRACT LAT/LNG ---------------- */
  const extractLatLng = (url) => {
    try {
      const match = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (match) {
        return { lat: match[1], lng: match[2] };
      }
    } catch {
      // Ignore URL parsing errors
    }
    return null;
  };

  /* ---------------- MASTER AMENITIES & SERVICES HANDLERS ---------------- */
  const toggleAmenity = (name) => {
    if (readOnly) return;
    setSelectedAmenities((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const selectAllAmenities = () => {
    if (readOnly) return;
    setSelectedAmenities(new Set(masterAmenities.map((a) => a.name)));
  };

  const clearAllAmenities = () => {
    if (readOnly) return;
    setSelectedAmenities(new Set());
  };

  const toggleService = (name) => {
    if (readOnly) return;
    setSelectedServices((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const selectAllServices = () => {
    if (readOnly) return;
    setSelectedServices(new Set(masterServices.map((s) => s.service_name)));
  };

  const clearAllServices = () => {
    if (readOnly) return;
    setSelectedServices(new Set());
  };

  const syncPropertyAmenitiesAndServices = async (targetSocietyId) => {
    try {
      if (enableAmenities && selectedAmenities.size > 0) {
        const existingAmenities = await getAmenities(targetSocietyId).catch(() => []);
        const existingNames = new Set(
          (existingAmenities || []).map((a) => (a.name || "").trim().toLowerCase())
        );

        for (const amenityName of selectedAmenities) {
          if (!existingNames.has(amenityName.trim().toLowerCase())) {
            const template = masterAmenities.find(
              (a) => (a.name || "").trim().toLowerCase() === amenityName.trim().toLowerCase()
            );
            await createAmenity({
              society_id: targetSocietyId,
              name: amenityName,
              description: template?.description || `${amenityName} facility`,
              rules: template?.rules || "Standard community guidelines apply.",
              type: template?.type || "activity",
              booking_fee: template?.booking_fee != null ? template.booking_fee : 0,
              max_booking_hours: template?.max_booking_hours || 2,
              advance_booking_days: template?.advance_booking_days || 30,
              booking_options: template?.booking_options || null,
            }).catch((err) => console.error("Failed to provision amenity:", amenityName, err));
          }
        }
      }

      if (enableServices && selectedServices.size > 0) {
        const existingServices = await getServices(targetSocietyId).catch(() => []);
        const existingServiceNames = new Set(
          (existingServices || []).map((s) => (s.service_name || "").trim().toLowerCase())
        );

        for (const serviceName of selectedServices) {
          if (!existingServiceNames.has(serviceName.trim().toLowerCase())) {
            await createService({
              society_id: targetSocietyId,
              service_name: serviceName,
            }).catch((err) => console.error("Failed to provision service:", serviceName, err));
          }
        }
      }
    } catch (syncErr) {
      console.error("Error provisioning amenities/services:", syncErr);
    }
  };

  /* ---------------- HANDLE CHANGE ---------------- */
  const handleChange = (e) => {
    if (readOnly) return;

    const { name, value } = e.target;

    let updatedValue = value;

    if (name === "contact_number") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    if (name === "pincode") {
      updatedValue = value.replace(/\D/g, "").slice(0, 6);
    }

    setForm((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleIconChange = (e) => {
    if (readOnly) return;
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setIconFile(file);
    if (!file) {
      setIconPreview(resolveBackendUrl(society?.icon_url || ""));
      return;
    }

    const url = URL.createObjectURL(file);
    setIconPreview(url);
  };

  /* ---------------- HANDLE MAP ---------------- */
  const handleMapChange = (e) => {
    if (readOnly) return;

    const value = e.target.value;
    const coords = extractLatLng(value);

    setForm((prev) => ({
      ...prev,
      google_map_url: value,
      latitude: coords?.lat || prev.latitude,
      longitude: coords?.lng || prev.longitude,
    }));
  };

  /* ---------------- VALIDATE ---------------- */
  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (readOnly) return;

    const nextForm = { ...form, [name]: value };
    const parsed = societySchema.safeParse(nextForm);
    if (parsed.success) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    const fieldErrors = zodErrorToFieldErrors(parsed.error);
    setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] || "" }));
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (readOnly) return;

    const parsed = societySchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors = zodErrorToFieldErrors(parsed.error);
      setErrors(fieldErrors);
      toast.error(Object.values(fieldErrors)[0] || "Please fix validation errors.");
      return;
    }

    try {
      setLoading(true);

      if (society) {
        await updateSociety(society.society_id, parsed.data);
        if (iconFile) {
          await uploadSocietyIcon(society.society_id, iconFile);
        }
        await syncPropertyAmenitiesAndServices(society.society_id);
        toast.success("Society updated successfully");
        onSuccess();
      } else {
        const res = await createSociety(parsed.data);
        const newSocietyId = res?.society_id || res?.data?.society_id;
        if (iconFile && newSocietyId) {
          await uploadSocietyIcon(newSocietyId, iconFile);
        }
        if (newSocietyId) {
          await syncPropertyAmenitiesAndServices(newSocietyId);
        }
        toast.success("Property created successfully");
        onSuccess(newSocietyId);
      }
    } catch (err) {
      // handled in service
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProperty = async () => {
    if (readOnly || !society?.society_id) return;

    const confirmed = window.confirm(
      `Delete ${society.society_name || "this property"}? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setDeleting(true);
      await deleteSociety(society.society_id);
      if (onDeleteSuccess) {
        onDeleteSuccess();
      } else if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      // handled in service
    } finally {
      setDeleting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>

      {/* GRID */}
      <div className={styles.grid}>

        <div className={styles.fullSpan}>
          <div className={styles.iconLabel}>Property Icon</div>
          <div className={styles.iconUploadRow}>
            {iconPreview ? (
              <img
                src={iconPreview}
                alt="Property icon preview"
                className={styles.iconPreview}
              />
            ) : (
              <div className={styles.iconPreviewPlaceholder}>No icon</div>
            )}

            <div className={styles.iconUploadControls}>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleIconChange}
                disabled={readOnly}
              />
              <div className={styles.iconHint}>
                Upload PNG/JPG/WEBP (recommended square image).
              </div>
            </div>
          </div>
        </div>

        <FormInput label="Society Name" name="society_name" value={form.society_name} onChange={handleChange} onBlur={handleBlur} error={errors.society_name} disabled={readOnly} required />

        <FormInput
          label="Property Type"
          name="society_type"
          value={form.society_type}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.society_type}
          disabled={readOnly}
          required
          select
          options={Array.from(new Set([...masterPropertyTypes, form.society_type])).filter(Boolean)}
          placeholder="Select Type..."
        />

        <FormInput
          label="Parent Property"
          name="parent_society_id"
          value={form.parent_society_id}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.parent_society_id}
          disabled={readOnly}
          select
          options={[
            { value: "", label: "None (Root Property)" },
            ...allSocieties
              .filter((s) => !society || String(s.society_id) !== String(society.society_id))
              .map((s) => ({
                value: String(s.society_id),
                label: `${s.society_name} (${s.society_type || "Property"})${s.parent_society_id ? " [Child]" : " [Root]"}`,
              })),
          ]}
          placeholder="None (Root Property)"
        />

        <FormInput
          label="Property Status"
          name="property_status"
          value={form.property_status}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.property_status}
          disabled={readOnly}
          select
          options={[
            { value: "operational", label: "Operational / Active" },
            { value: "under_construction", label: "Under Construction" },
            { value: "planned", label: "Planned / Upcoming" },
            { value: "inactive", label: "Inactive" },
          ]}
          placeholder="Select Status..."
        />

        <FormInput label="Contact Number" name="contact_number" value={form.contact_number} onChange={handleChange} onBlur={handleBlur} error={errors.contact_number} disabled={readOnly} required />

        <FormInput label="Email" name="contact_email" value={form.contact_email} onChange={handleChange} onBlur={handleBlur} error={errors.contact_email} disabled={readOnly} />

        <FormInput label="City" name="city" value={form.city} onChange={handleChange} onBlur={handleBlur} error={errors.city} disabled={readOnly} required />

        <FormInput label="State" name="state" value={form.state} onChange={handleChange} disabled={readOnly} />

        <FormInput label="Country" name="country" value={form.country} onChange={handleChange} disabled={readOnly} />

        <FormInput label="Pincode" name="pincode" value={form.pincode} onChange={handleChange} onBlur={handleBlur} error={errors.pincode} disabled={readOnly} />

        <FormInput label="Google Map URL" name="google_map_url" value={form.google_map_url} onChange={handleMapChange} disabled={readOnly} />

        <FormInput label="Latitude" name="latitude" value={form.latitude} onChange={handleChange} onBlur={handleBlur} error={errors.latitude} disabled={readOnly} />

        <FormInput label="Longitude" name="longitude" value={form.longitude} onChange={handleChange} onBlur={handleBlur} error={errors.longitude} disabled={readOnly} />

      </div>

      {/* MASTER AMENITIES PROVISIONING */}
      <div className={styles.masterSectionCard}>
        <div className={styles.masterHeader}>
          <div className={styles.masterHeaderLeft}>
            <div className={styles.masterIconBadge} style={{ background: "rgba(37, 99, 235, 0.1)", color: "var(--color-dark-bg)" }}>
              🏊
            </div>
            <div>
              <h4 className={styles.masterTitle}>Property Amenities</h4>
              <p className={styles.masterSubtitle}>
                Enable amenities for this property and select items from the master catalog.
              </p>
            </div>
          </div>
          <label className={styles.toggleSwitch}>
            <input
              type="checkbox"
              checked={enableAmenities}
              onChange={(e) => setEnableAmenities(e.target.checked)}
              disabled={readOnly}
            />
            <span className={styles.toggleSlider}></span>
          </label>
        </div>

        {enableAmenities && (
          <div className={styles.masterContent}>
            <div className={styles.quickActionsBar}>
              <div className={styles.quickActionsGroup}>
                <button type="button" className={styles.quickActionLink} onClick={selectAllAmenities} disabled={readOnly}>
                  Select All
                </button>
                <span style={{ color: "#cbd5e1" }}>•</span>
                <button type="button" className={styles.quickActionLink} onClick={clearAllAmenities} disabled={readOnly}>
                  Clear All
                </button>
              </div>
              <span className={styles.selectionCountBadge}>
                {selectedAmenities.size} of {masterAmenities.length} selected
              </span>
            </div>

            <div className={styles.chipsGrid}>
              {masterAmenities.map((amenity) => {
                const isSelected = selectedAmenities.has(amenity.name);
                return (
                  <div
                    key={amenity.name}
                    className={`${styles.chipItem} ${isSelected ? styles.chipItemActive : ""}`}
                    onClick={() => toggleAmenity(amenity.name)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => { }}
                      className={styles.chipCheckbox}
                      disabled={readOnly}
                    />
                    <span>{amenity.icon || "✨"}</span>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {amenity.name}
                    </span>
                  </div>
                );
              })}
            </div>

            
          </div>
        )}
      </div>

      {/* MASTER SERVICES PROVISIONING */}
      <div className={styles.masterSectionCard}>
        <div className={styles.masterHeader}>
          <div className={styles.masterHeaderLeft}>
            <div className={styles.masterIconBadge} style={{ background: "rgba(16, 185, 129, 0.1)", color: "#047857" }}>
              🔧
            </div>
            <div>
              <h4 className={styles.masterTitle}>Property Services</h4>
              <p className={styles.masterSubtitle}>
                Enable maintenance & operational services for this property from the master catalog.
              </p>
            </div>
          </div>
          <label className={styles.toggleSwitch}>
            <input
              type="checkbox"
              checked={enableServices}
              onChange={(e) => setEnableServices(e.target.checked)}
              disabled={readOnly}
            />
            <span className={styles.toggleSlider}></span>
          </label>
        </div>

        {enableServices && (
          <div className={styles.masterContent}>
            <div className={styles.quickActionsBar}>
              <div className={styles.quickActionsGroup}>
                <button type="button" className={styles.quickActionLink} onClick={selectAllServices} disabled={readOnly}>
                  Select All
                </button>
                <span style={{ color: "#cbd5e1" }}>•</span>
                <button type="button" className={styles.quickActionLink} onClick={clearAllServices} disabled={readOnly}>
                  Clear All
                </button>
              </div>
              <span className={styles.selectionCountBadge}>
                {selectedServices.size} of {masterServices.length} selected
              </span>
            </div>

            <div className={styles.chipsGrid}>
              {masterServices.map((service) => {
                const isSelected = selectedServices.has(service.service_name);
                return (
                  <div
                    key={service.service_name}
                    className={`${styles.chipItem} ${isSelected ? styles.chipItemActive : ""}`}
                    onClick={() => toggleService(service.service_name)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => { }}
                      className={styles.chipCheckbox}
                      disabled={readOnly}
                    />
                    <span>{service.icon || "⚡"}</span>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {service.service_name}
                    </span>
                  </div>
                );
              })}
            </div>

            
          </div>
        )}
      </div>
      {/* ADDRESS */}
      <FormInput
        label="Address"
        name="address"
        value={form.address}
        onChange={handleChange}
        textarea
        disabled={readOnly}
      />

      {/* MAP */}
      {form.latitude && form.longitude && (
        <div className={styles.map}>
          <iframe
            title="map"
            width="100%"
            height="100%"
            loading="lazy"
            src={`https://maps.google.com/maps?q=${form.latitude},${form.longitude}&z=15&output=embed`}
          />
        </div>
      )}

      {/* BUTTON */}
      {!readOnly && (
        <div className={styles.actions}>
          {society?.society_id && (
            <Button
              type="button"
              variant="danger"
              loading={deleting}
              disabled={loading}
              onClick={handleDeleteProperty}
            >
              Delete Property
            </Button>
          )}

          <Button type="submit" variant="primary" loading={loading} disabled={deleting}>
            {society ? "Update Property" : "Create Property"}
          </Button>
        </div>
      )}

    </form>
  );
};

export default SocietyForm;
